import styled, {keyframes} from 'styled-components';
import {ReactNode} from 'react';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translate3d(0, 10px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

const nudge = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-3px); }
  40% { transform: translateX(3px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
`;

const Wrapper = styled.div<{$compact: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(p) => (p.$compact ? 10 : 14)}px;
  width: 100%;
  padding: ${(p) => (p.$compact ? '28px 16px' : '48px 24px')};
  text-align: center;
  animation: ${fadeInUp} ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const IllustrationWrapper = styled.div<{$compact: boolean}>`
  position: relative;
  width: ${(p) => (p.$compact ? 64 : 88)}px;
  height: ${(p) => (p.$compact ? 64 : 88)}px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.dangerSoft};
  border: 1px solid rgba(225, 1, 1, 0.16);
  animation: ${nudge} 620ms ${(p) => p.theme.motion.easing.standard} 260ms 1;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Title = styled.p`
  margin: 0;
  color: ${(p) => p.theme.colors.primary500};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.2px;
`;

const Description = styled.p`
  margin: 0;
  color: ${(p) => p.theme.colors.textMuted};
  font-size: 13px;
  line-height: 1.65;
  max-width: 380px;
`;

const RetryButton = styled.button`
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.theme.colors.border};
  background: ${(p) => p.theme.colors.neutral100};
  color: ${(p) => p.theme.colors.primary500};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${(p) => p.theme.elevation.sm};
  transition: transform ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    box-shadow ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    border-color ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out};

  svg {
    transition: transform ${(p) => p.theme.motion.duration.slow}
      ${(p) => p.theme.motion.easing.out};
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(p) => p.theme.elevation.md};
    border-color: ${(p) => p.theme.colors.violet300};
  }

  &:hover svg {
    transform: rotate(-180deg);
  }

  &:active {
    transform: translateY(0) scale(0.97);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &:active {
      transform: none;
    }

    &:hover svg {
      transform: none;
    }
  }
`;

const AlertIcon = ({size}: {size: number}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 8.5v4.5m0 3.5v.01M10.3 3.4 2.6 17a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 3.4a2 2 0 0 0-3.4 0Z"
      stroke="#E10101"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RetryIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M20 12a8 8 0 1 1-2.3-5.6M20 4v4.5h-4.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ErrorStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  compact?: boolean;
  className?: string;
}

export const ErrorState = ({
  title,
  description,
  icon,
  onRetry,
  retryLabel,
  compact = false,
  className,
}: ErrorStateProps) => (
  <Wrapper $compact={compact} className={className} role="alert">
    <IllustrationWrapper $compact={compact}>
      {icon ?? <AlertIcon size={compact ? 26 : 32} />}
    </IllustrationWrapper>
    <Title>{title}</Title>
    {description ? <Description>{description}</Description> : null}
    {onRetry ? (
      <RetryButton type="button" onClick={onRetry}>
        <RetryIcon />
        {retryLabel}
      </RetryButton>
    ) : null}
  </Wrapper>
);
